if (!customElements.get('order-tracking')) {
	const CARRIERS = {
		ups: {
			name: 'UPS',
			url: (n) => `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(n)}`
		},
		usps: {
			name: 'USPS',
			url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`
		}
	};

	// Strip spaces/dashes, and the "420 + ZIP" prefix USPS barcodes carry when scanned.
	const normalize = (value) => {
		let n = value.toUpperCase().replace(/[^0-9A-Z]/g, '');
		if (/^420\d{5}(9\d{19,21})$/.test(n)) n = n.slice(8);
		else if (/^420\d{9}(9\d{19,21})$/.test(n)) n = n.slice(12);
		return n;
	};

	const detect = (n) => {
		if (/^1Z[0-9A-Z]{16}$/.test(n)) return 'ups';
		if (/^9\d{19,21}$/.test(n)) return 'usps';
		if (/^(82|03|23|70)\d{18}$/.test(n)) return 'usps';
		if (/^[A-Z]{2}\d{9}US$/.test(n)) return 'usps';
		return null;
	};

	class OrderTracking extends HTMLElement {
		connectedCallback() {
			this.form = this.querySelector('[data-ot-form]');
			this.input = this.querySelector('[data-ot-input]');
			this.error = this.querySelector('[data-ot-error]');
			this.detected = this.querySelector('[data-ot-detected]');
			this.result = this.querySelector('[data-ot-result]');
			this.waiting = this.querySelector('.ot-waiting');
			this.strings = this.readStrings();

			const known = document.querySelector(`[data-ot-known="${this.dataset.section}"]`);
			try { this.known = known ? JSON.parse(known.textContent) : {}; } catch (e) { this.known = {}; }

			this.input.addEventListener('input', () => this.updateDetected());
			this.form.addEventListener('submit', (e) => {
				e.preventDefault();
				this.track();
			});

			const param = new URLSearchParams(window.location.search).get('tracking');
			if (param) {
				this.input.value = param;
				this.updateDetected();
				this.track(false);
			}
		}

		// Stage titles live in the server-rendered cards; reuse them so editor changes apply here too.
		readStrings() {
			const section = this.closest('.shopify-section') || document;
			const data = section && section.querySelector('script[data-ot-strings]');
			try { return data ? JSON.parse(data.textContent) : {}; } catch (e) { return {}; }
		}

		updateDetected() {
			const found = detect(normalize(this.input.value));
			this.detected.hidden = !found;
			if (found) this.detected.textContent = CARRIERS[found].name;
			this.showError('');
		}

		showError(message) {
			this.error.textContent = message;
			this.error.hidden = !message;
		}

		track(focus = true) {
			const number = normalize(this.input.value);
			if (number.length < 8) {
				this.showError('Enter the full tracking number from your shipping email.');
				return;
			}
			const carrierKey = detect(number);
			if (!carrierKey) {
				this.showError("We couldn't recognize that USPS or UPS tracking number. Double-check it against your shipping email, or message us below.");
				return;
			}
			this.showError('');
			this.render(number, carrierKey, focus);

			const url = new URL(window.location.href);
			url.searchParams.set('tracking', number);
			window.history.replaceState({}, '', url);
		}

		render(number, carrierKey, focus) {
			const carrier = CARRIERS[carrierKey];
			const match = this.known[number];
			const stage = match ? Math.max(3, match.stage) : 3;
			const preorder = match ? match.preorder : false;
			const s = this.strings;

			const card = this.result.querySelector('[data-ot-tracker]');
			card.dataset.stage = stage;
			card.toggleAttribute('data-preorder', preorder);
			card.querySelector('[data-ot-eyebrow]').textContent = match ? `Order ${match.order}` : `${carrier.name} shipment`;
			card.querySelector('[data-ot-title]').textContent = stage === 4 ? s.stage_4_title : s.stage_3_title;
			card.querySelector('[data-ot-text]').textContent = stage === 4
				? s.stage_4_text
				: `Your package is with ${carrier.name} and headed your way. Tap below for live scan updates — brand-new labels can take 24–48 hours to show their first scan.`;
			card.querySelector('[data-ot-step2]').textContent = preorder ? s.step_2_preorder_label : s.step_2_label;

			card.querySelectorAll('.ot-step').forEach((step, i) => {
				const n = i + 1;
				step.classList.toggle('is-done', n < stage || (n === 4 && stage === 4));
				step.classList.toggle('is-active', n === stage);
			});
			card.querySelector('.ot-steps').style.setProperty('--ot-progress', `${((stage - 1) / 3) * 100}%`);

			card.querySelector('[data-ot-meta]').hidden = false;
			card.querySelector('[data-ot-carrier]').textContent = carrier.name;
			card.querySelector('[data-ot-number]').textContent = number;
			card.querySelector('[data-ot-link]').href = carrier.url(number);

			// Restart the entrance animation on each lookup.
			this.result.hidden = true;
			void this.result.offsetWidth;
			this.result.hidden = false;
			this.waiting.hidden = true;
			if (focus) this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}

	customElements.define('order-tracking', OrderTracking);

	document.addEventListener('click', (e) => {
		const toggle = e.target.closest('[data-ot-open-form]');
		if (!toggle) return;
		const panel = document.getElementById(toggle.getAttribute('aria-controls'));
		if (!panel) return;
		panel.hidden = !panel.hidden;
		toggle.setAttribute('aria-expanded', !panel.hidden);
		if (!panel.hidden) {
			const tracking = new URLSearchParams(window.location.search).get('tracking');
			const body = panel.querySelector('textarea');
			if (tracking && body && !body.value) body.value = `Tracking number: ${tracking}\n\n`;
			panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
			const first = panel.querySelector('input:not([type=hidden]), textarea');
			if (first) setTimeout(() => first.focus({ preventScroll: true }), 300);
		}
	});

	// Keep the message form open after a submit so the success/error notice is visible.
	document.querySelectorAll('[data-ot-contact]').forEach((panel) => {
		if (!panel.querySelector('.form-notification')) return;
		panel.hidden = false;
		const toggle = document.querySelector(`[aria-controls="${panel.id}"]`);
		if (toggle) toggle.setAttribute('aria-expanded', 'true');
	});
}
